import React, { useEffect, useState } from 'react';
import { CloudIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { classNames } from '../../components/helpers/functions';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import BetaAttributes, { configurePayload } from '../../components/Attributes';

import useModal from '../../hooks/useModal';
import { useRequiredBeta } from '../../hooks/useRequired';
import useAsync from '../../hooks/useAsync';

import { addContract, getContract, updateContract } from '../../api/contracts';
import { getModuleFieldsBeta } from '../../api/modules';

import PDFModal from '../../modals/PDFModal';

const CONTRACT_MODULE_ID = process.env.REACT_APP_MODULE_ID_CONTRACT;

const ContractForm = () => {
	const navigate = useNavigate();

	const { id } = useParams();

	const [confirmModal, setConfirmModal] = useState(false);

	const {
		setClose: onCloseUploadContractPdfModal,
		setOpen: onOpenUploadContractPdfModal,
	} = useModal('uploadContractPdfModal');
	const {
		open: addServiceModalOptions,
		setOpen: onOpenAddContractServiceModal,
		setClose: onCloseAddContractServiceModal,
	} = useModal('addContractServiceModal');

	const { value: moduleFields, status: isLoadingModuleFields } = useAsync(
		getModuleFieldsBeta,
		{
			defaultPayload: { id: CONTRACT_MODULE_ID, active: true },
		}
	);

	const [form, setForm] = useState({});
	const [updatedForm, setUpdateForm] = useState({});

	const hasAllRequired = useRequiredBeta(
		{ ...form, ...updatedForm },
		moduleFields?.combined
			?.filter((field) => field.isRequired)
			.map((field) => field.key)
	);

	const { acceptedFiles, isDragActive, getRootProps, getInputProps } =
		useDropzone({
			accept: 'application/pdf',
			maxFiles: 1,
		});

	const { execute: handleAddContract, status: addingContract } = useAsync(
		addContract,
		{ immediate: false }
	);

	const { execute: handleUpdateContract, status: updatingContract } = useAsync(
		updateContract,
		{ immediate: false }
	);

	const {
		value: contract,
		execute: onRefreshContract,
		status: isLoadingContract,
	} = useAsync(getContract, {
		immediate: !!id,
		...(id
			? {
					defaultPayload: {
						id,
					},
			  }
			: {}),
	});

	const onAddContract = ({ files = [] }) => {
		const contractObject = {
			...configurePayload(updatedForm, moduleFields?.combined),
		};

		handleAddContract(
			{
				...(files?.length
					? {
							files: acceptedFiles,
							bucket: 'local-contracts',
							information: contractObject,
					  }
					: contractObject),
			},
			() => {
				onCloseUploadContractPdfModal(false);
				navigate('/manage/contracts');
			}
		);
	};

	const onUpdateContract = ({ files = [] }) => {
		const contractObject = {
			id,
			...configurePayload({ ...updatedForm }, moduleFields?.combined),
		};
		handleUpdateContract(
			{
				...(files?.length
					? {
							files: acceptedFiles,
							bucket: 'local-contracts',
							information: contractObject,
					  }
					: contractObject),
			},
			() => {
				onCloseUploadContractPdfModal(false);
				navigate('/manage/contracts');
			}
		);
	};

	useEffect(() => {
		if (contract && moduleFields) {
			// 1. On update attempt, create update object
			let update = {};

			if (contract?.documents?.length) {
				onOpenUploadContractPdfModal({
					file: contract?.documents[0]?.location,
				});
			}

			const CONVERTER = {
				vendor: 'contacts',
				'coverage-start': 'startDate',
				category: 'categoryId',
			};

			// Update update object with event fields
			moduleFields?.combined?.map((field) => {
				const key = CONVERTER[field.key] || field?.key;

				if (contract[key]) {
					update[field?.key] = {
						fieldId: field?.id,
						value: contract[key],
					};
				}

				if (field?.key === 'duration') {
					update[field?.key] = {
						fieldId: field?.id,
						value: Math.abs(
							moment(moment(contract?.endDate).add(1, 'd').toDate()).diff(
								contract?.startDate,
								'month',
								false
							)
						),
					};
				}
			});

			if (contract?.contacts?.length) {
				update['vendor'] = {
					value: contract?.contacts[0]?.id,
				};
			}

			if (update?.assets?.value?.length) {
				update['assets'] = {
					...update.assets,
					value: update?.assets?.value?.map((asset) => ({
						asset,
						label: `${asset?.manufacturer?.name} ${asset?.model?.name}`,
						value: asset?.id,
					})),
				};
			}

			setForm({ ...update });
		}
	}, [contract, moduleFields]);

	useEffect(() => {
		if (acceptedFiles?.length) {
			onOpenUploadContractPdfModal(true);
		}
	}, [acceptedFiles]);

	const skipModuleKeys = [...(id ? ['coverage-start', 'duration'] : [])];

	const ContractFormContent = ({ full = false } = {}) => (
		<>
			{isLoadingModuleFields === 'pending' ? (
				<LoadingSkeleton />
			) : (
				<>
					<div className="grid grid-cols-2 gap-y-4 gap-x-4 text-left">
						{moduleFields?.standard
							?.filter((field) => skipModuleKeys.indexOf(field.key) === -1)
							?.map((field) => (
								<BetaAttributes
									field={field}
									onChange={(index, value, fullValue) => {
										if (value || fullValue) {
											let updatedValue = form[index]?.value;

											if (fullValue) {
												updatedValue = fullValue;
											} else {
												if (updatedValue && Array.isArray(value)) {
													updatedValue = [
														...(Array.isArray(updatedValue)
															? updatedValue
															: [updatedValue]),
														...value,
													];
												} else {
													updatedValue = value;
												}
											}

											setUpdateForm({
												...updatedForm,
												[index]: {
													value: updatedValue,
													fieldId: field.id,
												},
											});
										} else {
											let updated = { ...updatedForm };
											updated[index] = {
												...updated[index],
												value: null,
											};

											setUpdateForm(updated);
										}
									}}
									form={{ ...form, ...updatedForm }}
									updatedForm={updatedForm}
									setForm={setUpdateForm}
									moduleId={CONTRACT_MODULE_ID}
								/>
							))}
					</div>
				</>
			)}
		</>
	);
	return (
		<>
			<div className="flex flex-1">
				<MainContent maxWidth="w-full xl:w-1/2">
					<div>
						<PageHeading
							divider={false}
							onBack={() => history.goBack()}
							actions={[
								{
									name: id ? 'Update contract' : 'Create contract',
									onClick: () => {
										if (id) {
											onUpdateContract({ files: acceptedFiles });
										} else {
											onAddContract({ files: acceptedFiles });
										}
									},
									disabled: !hasAllRequired,
								},
							]}
						>
							{id ? 'Update contract' : 'Create a new contract'}
						</PageHeading>

						<div className={classNames('m-auto mb-4 ')}>
							<div className="pb-5 ">
								{acceptedFiles[0] || contract?.documents[0]?.location ? (
									<div
										onClick={() => onOpenUploadContractPdfModal(true)}
										className="w-full border-2 border-dashed rounded-lg flex items-center justify-center py-12 text-center text-xs text-gray-700 cursor-pointer"
									>
										{' '}
										<div className="drop-container text-center flex flex-col items-center justify-center">
											<DocumentCheckIcon className="w-7 h-7 text-red-600 mb-2" />{' '}
											View Uploaded PDF
										</div>
									</div>
								) : !id ? (
									<div
										className="w-full border-2 border-dashed rounded-lg flex items-center justify-center py-12 text-center text-xs text-gray-700 cursor-pointer"
										{...getRootProps()}
										style={{ marginBottom: 15 }}
									>
										<input {...getInputProps()} />
										{isDragActive ? (
											<p>Place files here</p>
										) : (
											<div className="drop-container text-center">
												<CloudIcon className={'w-12 h-12 m-auto mb-2'} />
												<p>Upload a PDF</p>
											</div>
										)}
									</div>
								) : null}
							</div>

							{ContractFormContent()}
						</div>
					</div>
				</MainContent>
			</div>
			<PDFModal
				setClose={() => {
					onCloseUploadContractPdfModal();
				}}
				options={{
					file: acceptedFiles[0] || contract?.documents[0]?.location,
					actions: [
						{
							label:
								addingContract === 'pending'
									? 'Uploading PDF...'
									: id
									? 'Update Contract'
									: 'Add Contract',
							disabled: !hasAllRequired,
							onClick: () => {
								if (id) {
									onUpdateContract({ files: acceptedFiles });
								} else {
									onAddContract({ files: acceptedFiles });
								}
							},
						},
					],
				}}
			>
				<>
					<div className={classNames('m-auto mb-4 w-full')}>
						{isLoadingModuleFields === 'pending' ? (
							<LoadingSkeleton />
						) : (
							<>
								<div className="grid grid-cols-2 gap-y-4 gap-x-4 text-left">
									{moduleFields?.standard
										?.filter(
											(field) => skipModuleKeys.indexOf(field.key) === -1
										)
										?.map((field) => (
											<BetaAttributes
												field={field}
												onChange={(index, value, fullValue) => {
													if (value || fullValue) {
														let updatedValue = form[index]?.value;

														if (fullValue) {
															updatedValue = fullValue;
														} else {
															if (updatedValue && Array.isArray(value)) {
																updatedValue = [
																	...(Array.isArray(updatedValue)
																		? updatedValue
																		: [updatedValue]),
																	...value,
																];
															} else {
																updatedValue = value;
															}
														}

														setUpdateForm({
															...updatedForm,
															[index]: {
																value: updatedValue,
																fieldId: field.id,
															},
														});
													} else {
														let updated = { ...updatedForm };
														updated[index] = {
															...updated[index],
															value: null,
														};

														setUpdateForm(updated);
													}
												}}
												form={{ ...form, ...updatedForm }}
												updatedForm={updatedForm}
												setForm={setUpdateForm}
												moduleId={CONTRACT_MODULE_ID}
											/>
										))}
								</div>
							</>
						)}
					</div>
				</>
			</PDFModal>
		</>
	);
};

export default ContractForm;
